import { useState, useCallback } from 'react';
import { getAuth } from 'firebase/auth';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Download, FileSpreadsheet } from 'lucide-react';
import { formatLocalDate } from '@/lib/localDate';

/**
 * QBO CSV Export dialog — opened from the Invoices list.
 *
 * Picks a date range (defaults to previous month) and downloads a native
 * QuickBooks Online invoice import CSV. If the response includes the
 * `X-Export-Warning: tax-codes-missing` header, surfaces a warning toast
 * so the user knows to populate the per-tenant qbo_tax_code_map.
 *
 * Round-trip dedup (R1): the backend marks every exported invoice with
 * qbo_csv_exported_at and, by default, EXCLUDES already-exported invoices on a
 * re-export so an overlapping date range never duplicates invoices in QBO.
 * The dialog runs a non-mutating count_only preview to show
 * "{n} new, {m} already exported (skipped)" before download, and exposes an
 * override checkbox ("Re-export already-exported invoices") that re-includes
 * them for legitimate re-export.
 *
 * See backend `services/qbo_csv_export.py` and
 * `services/project-tracker/docs/qbo-csv-export.md`.
 */

function previousMonthRange(today = new Date()): { from: string; to: string } {
  const year = today.getFullYear();
  const month = today.getMonth(); // 0-based current month
  // First day of previous month
  const firstPrev = new Date(year, month - 1, 1);
  // Last day of previous month = day 0 of current month
  const lastPrev = new Date(year, month, 0);
  return { from: formatLocalDate(firstPrev), to: formatLocalDate(lastPrev) };
}

function triggerBlobDownload(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

interface ExportCounts {
  new_count: number;
  skipped_count: number;
  invoice_count: number;
}

export interface QboCsvExportDialogProps {
  /** Optional override for the trigger button (otherwise the default outline button is rendered). */
  trigger?: React.ReactNode;
}

export function QboCsvExportDialog({ trigger }: QboCsvExportDialogProps) {
  const defaults = previousMonthRange();
  const [open, setOpen] = useState(false);
  const [fromDate, setFromDate] = useState(defaults.from);
  const [toDate, setToDate] = useState(defaults.to);
  const [includeAlreadyExported, setIncludeAlreadyExported] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [counts, setCounts] = useState<ExportCounts | null>(null);
  const [error, setError] = useState<string | null>(null);

  const buildUrl = useCallback(
    (extra: Record<string, string> = {}) => {
      const apiBase = import.meta.env.VITE_API_URL ?? '';
      const params = new URLSearchParams({
        from: fromDate,
        to: toDate,
        ...extra,
      });
      if (includeAlreadyExported) {
        params.set('include_already_exported', 'true');
      }
      return `${apiBase}/api/invoices/export/qbo-csv?${params.toString()}`;
    },
    [fromDate, toDate, includeAlreadyExported],
  );

  const datesValid = (): boolean => {
    if (!fromDate || !toDate) {
      setError('Both From and To dates are required.');
      return false;
    }
    if (fromDate > toDate) {
      setError('From date must be on or before To date.');
      return false;
    }
    return true;
  };

  // Non-mutating preview: ask the backend how many invoices are new vs already
  // exported for the selected range without generating the file or marking.
  const handlePreview = async () => {
    setError(null);
    setCounts(null);
    if (!datesValid()) {
      return;
    }
    const user = getAuth().currentUser;
    if (!user) {
      setError('Not signed in.');
      return;
    }
    setPreviewing(true);
    try {
      const token = await user.getIdToken();
      const res = await fetch(buildUrl({ count_only: 'true' }), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(
          (json as { detail?: string }).detail ||
            `Preview failed (HTTP ${res.status})`,
        );
      }
      const data = (await res.json()) as ExportCounts;
      setCounts({
        new_count: data.new_count,
        skipped_count: data.skipped_count,
        invoice_count: data.invoice_count,
      });
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Preview failed.';
      setError(message);
    } finally {
      setPreviewing(false);
    }
  };

  const handleDownload = async () => {
    setError(null);
    if (!datesValid()) {
      return;
    }

    const user = getAuth().currentUser;
    if (!user) {
      setError('Not signed in.');
      return;
    }

    setDownloading(true);
    try {
      const token = await user.getIdToken();
      const res = await fetch(buildUrl(), {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(
          (json as { detail?: string }).detail ||
            `Export failed (HTTP ${res.status})`,
        );
      }

      const fileName =
        res.headers.get('Content-Disposition')?.match(/filename="(.+)"/)?.[1] ??
        `qbo-invoices-${fromDate}-${toDate}.csv`;

      const warning = res.headers.get('X-Export-Warning');
      const newCount = res.headers.get('X-Export-New-Count');
      const skippedCount = res.headers.get('X-Export-Skipped-Count');
      const blob = await res.blob();
      triggerBlobDownload(blob, fileName);

      if (warning === 'tax-codes-missing') {
        toast.warning(
          'Some line items are missing tax codes — review the file and configure the tax map in Tenant Settings if needed.',
          { duration: 8000 },
        );
      } else {
        const detail =
          newCount !== null
            ? ` ${newCount} new, ${skippedCount ?? '0'} already exported (skipped).`
            : '';
        toast.success(`QBO CSV exported.${detail}`);
      }

      setOpen(false);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Export failed.';
      setError(message);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button variant="outline" data-testid="qbo-csv-export-trigger">
            <FileSpreadsheet className="mr-2 h-4 w-4" />
            Export to CSV (for QBO)
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Export invoices to QuickBooks CSV</DialogTitle>
          <DialogDescription>
            Downloads a QuickBooks Online–compatible CSV for the selected date range.
            Defaults to last month. Sent and paid invoices only. Cap: 80 invoices per file.
            Already-exported invoices are skipped by default so re-exporting an
            overlapping range never duplicates invoices in QuickBooks.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid gap-2">
            <Label htmlFor="qbo-export-from">From</Label>
            <Input
              id="qbo-export-from"
              type="date"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setCounts(null);
              }}
              data-testid="qbo-csv-export-from"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="qbo-export-to">To</Label>
            <Input
              id="qbo-export-to"
              type="date"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                setCounts(null);
              }}
              data-testid="qbo-csv-export-to"
            />
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="qbo-export-include-already"
              checked={includeAlreadyExported}
              onCheckedChange={(checked) => {
                setIncludeAlreadyExported(checked === true);
                setCounts(null);
              }}
              data-testid="qbo-csv-export-include-already"
            />
            <Label
              htmlFor="qbo-export-include-already"
              className="text-sm font-normal"
            >
              Re-export already-exported invoices
            </Label>
          </div>

          <div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handlePreview}
              disabled={previewing || downloading}
              data-testid="qbo-csv-export-preview"
            >
              {previewing ? 'Checking…' : 'Preview counts'}
            </Button>
            {counts && (
              <p
                className="mt-1 text-sm text-muted-foreground"
                data-testid="qbo-csv-export-counts"
              >
                {counts.new_count} new, {counts.skipped_count} already exported (skipped)
              </p>
            )}
          </div>

          {error && (
            <p className="text-sm text-destructive" data-testid="qbo-csv-export-error">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={downloading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDownload}
            disabled={downloading}
            data-testid="qbo-csv-export-download"
          >
            <Download className="mr-2 h-4 w-4" />
            {downloading ? 'Downloading…' : 'Download'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
